import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, KeyRound, Mail, Utensils } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientPortalLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    accessCode: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.accessCode) {
      toast({ title: 'Please enter your email and access code', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/client-portal/login`, {
        email: formData.email.toLowerCase(),
        access_code: formData.accessCode.toUpperCase()
      });

      // Store session data
      localStorage.setItem('clientPortalToken', response.data.token);
      localStorage.setItem('clientPortalEmail', response.data.client_email);
      localStorage.setItem('clientPortalContract', JSON.stringify(response.data.contract));

      toast({ title: 'Welcome! Redirecting to your contract...' });
      navigate(`/client-portal/contract/${response.data.contract.id}`);
    } catch (error) {
      console.error('Login error:', error);
      toast({ 
        title: 'Login failed', 
        description: error.response?.data?.detail || 'Invalid email or access code',
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e3a5f] to-[#2d6a4f] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-none">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center">
            <Utensils className="text-white" size={32} />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1e3a5f]">Client Portal</CardTitle>
          <p className="text-gray-600 mt-2">View your event contract and details</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail size={16} />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="client-email-input"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="accessCode" className="flex items-center gap-2">
                <KeyRound size={16} />
                Access Code
              </Label>
              <Input
                id="accessCode"
                type="text"
                data-testid="client-access-code-input"
                placeholder="Enter your access code"
                value={formData.accessCode}
                onChange={(e) => setFormData({ ...formData, accessCode: e.target.value.toUpperCase() })}
                className="mt-1 font-mono text-lg tracking-wider"
                maxLength={8}
              />
              <p className="text-xs text-gray-500 mt-1">
                The access code was provided by Willow Ridge Catering
              </p>
            </div>

            <Button
              type="submit"
              data-testid="client-login-btn"
              disabled={loading}
              className="w-full bg-[#1e3a5f] hover:bg-[#15422a] text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Access My Contract'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-sm text-gray-600">
              Don't have an access code?
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Contact Willow Ridge Catering to request one.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientPortalLogin;
